import { getBedbankChildMaxAge, getBundleAndSaveMaxChildAge, getBundleAndSaveMaxInfantAge, getBundleAndSaveMaxTeenagerAge } from 'lib/offer/offerUtils'
import { isNonEmptyArray, isShallowEqual, max, min, nonNullable } from 'lib/array/arrayUtils'
import { MAX_ADULTS } from 'constants/offer'
import { maxFlightChildAge, maxFlightInfantAge } from 'constants/flight'
import { formatInlineList } from 'lib/format/formatList'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { getPlural } from 'lib/string/pluralize'
import { validateFlightOccupants } from 'lib/flights/flightUtils'
import config from 'constants/config'

interface CountOccupantsOptions {
  maxChildAge?: number
  maxInfantAge?: number
  maxTeenagerAge?: number
}

export type AdultLabel = 'adult' | 'traveller'

export function occupancyMatchDefault(occupancy: Array<App.Occupants>) {
  return occupancy.length === 1 &&
    occupancy[0].adults === config.search.defaultOccupants.adults &&
    occupancy[0].teenagers === config.search.defaultOccupants.teenagers &&
    occupancy[0].children === config.search.defaultOccupants.children &&
    occupancy[0].infants === config.search.defaultOccupants.infants
}

/**
 * Sum up all occupancies given into a single aggregated occupancy item
 **/
export function countOccupants(
  occupancies: Array<App.Occupants>,
  { maxInfantAge, maxChildAge, maxTeenagerAge }: CountOccupantsOptions = {},
): Required<App.Occupants> {
  const shouldCategorizeChildAges = !(maxInfantAge == null && maxChildAge == null && maxTeenagerAge == null)

  const effectiveMaxInfantAge = maxInfantAge ?? maxFlightInfantAge
  const effectiveMaxChildAge = maxChildAge ?? maxFlightChildAge
  const effectiveMaxTeenagerAge = maxTeenagerAge ?? 0

  return occupancies.reduce<Required<App.Occupants>>((acc, occupancy) => {
    // In some places we're using a simple count for children,
    // in other places we're using an array of child ages
    const nonAdultAges = occupancy.childrenAge ?? []

    acc.adults += (occupancy.adults ?? 0)

    if (!shouldCategorizeChildAges || nonAdultAges.length === 0) {
      acc.teenagers += (occupancy.teenagers ?? 0)
      acc.children += (occupancy.children ?? nonAdultAges.length ?? 0)
      acc.infants += (occupancy.infants ?? 0)
      acc.childrenAge.push(...nonAdultAges)
    } else {
      for (const age of nonAdultAges) {
        if (age <= effectiveMaxInfantAge) {
          acc.infants++
          acc.childrenAge.push(age)
        } else if (age <= effectiveMaxChildAge) {
          acc.children++
          acc.childrenAge.push(age)
        } else if (age <= effectiveMaxTeenagerAge) {
          acc.teenagers++
          acc.childrenAge.push(age)
        } else {
          acc.adults++
        }
      }
    }
    return acc
  }, {
    adults: 0,
    children: 0,
    infants: 0,
    teenagers: 0,
    childrenAge: [],
    childrenBirthDate: [],
  })
}

export function sumUpOccupancies(
  occupancies: Array<App.Occupants> = [],
  target: Set<App.OccupantType> = new Set(['adults', 'children', 'infants', 'teenagers']),
  options: CountOccupantsOptions = {},
): number {
  let total = 0

  const {
    adults,
    children,
    infants,
    teenagers,
  } = countOccupants(occupancies, options)

  if (target.has('adults')) total += adults
  if (target.has('children')) total += children
  if (target.has('infants')) total += infants
  if (target.has('teenagers')) total += teenagers

  return total
}

// TODO: move the following funcs into a separate location relevant to Tours.V2OrderItemTravellerDetails space
export function countTravellers(travellers: Array<App.Tours.V2OfferSnapshotPassenger>) {
  const travellersCount: Record<string, number> = {}

  for (let i = 0; i < travellers.length; ++i) {
    travellersCount[travellers[i].type] = (travellersCount[travellers[i].type] || 0) + 1
  }

  return travellersCount
}

export function countTravellersAsOccupants(travellers: Array<App.Tours.V2OfferSnapshotPassenger | App.Tours.TourV2OrderItemTravellerDetails | App.FlightTraveller>): App.Occupants {
  return {
    adults: travellers.filter(t => t.type === 'adult').length,
    children: travellers.filter(t => t.type === 'child').length,
    infants: travellers.filter(t => t.type === 'infant').length,
    teenagers: travellers.filter(t => t.type === 'teenager').length,
    childrenAge: [],
  }
}

export function formatTourTravellers(travellers: Array<App.Tours.V2OfferSnapshotPassenger>) {
  const intl = getCurrentIntl()
  const travellersCount = countTravellers(travellers)
  const travellerTypes = Object.keys(travellersCount)
  const formatTravellerTypeCount = (type: string, count: number): string => {
    switch (type) {
      case 'adult': return intl.formatMessage({ id: 'gn9h9avB2S6ie3yqrVUr', defaultMessage: '{count, plural, one {# adult} other {# adults}}', description: 'Pluralised count of adult travellers in the tour booking occupancy display' }, { count })
      case 'Adult': return intl.formatMessage({ id: 'mdVruKYA0-uN2Jaeo_vk', defaultMessage: '{count, plural, one {# Adult} other {# Adults}}', description: 'Pluralised count of adult travellers in the tour booking occupancy summary' }, { count })
      case 'child': return intl.formatMessage({ id: 'dz8pFEJ3vQRkBsMMtEyE', defaultMessage: '{count, plural, one {# child} other {# children}}', description: 'Pluralised count of child travellers in the tour booking occupancy display' }, { count })
      case 'Child': return intl.formatMessage({ id: 'RoBCVWxry6FrfmGr9bn0', defaultMessage: '{count, plural, one {# Child} other {# Children}}', description: 'Pluralised count of child travellers in the tour booking occupancy summary' }, { count })
      case 'infant': return intl.formatMessage({ id: 'Wh9_O6zgBqnQOCPkP4k7', defaultMessage: '{count, plural, one {# infant} other {# infants}}', description: 'Pluralised count of infant travellers in the tour booking occupancy display' }, { count })
      case 'Infant': return intl.formatMessage({ id: 'nLGAm-kwG2L6HTSqkOwe', defaultMessage: '{count, plural, one {# Infant} other {# Infants}}', description: 'Pluralised count of infant travellers in the tour booking occupancy summary' }, { count })
      case 'teenager': return intl.formatMessage({ id: 'Xth5w6jSAEbLqSQA8pMJ', defaultMessage: '{count, plural, one {# teenager} other {# teenagers}}', description: 'Pluralised count of teenager travellers in the tour booking occupancy display' }, { count })
      case 'Teenager': return intl.formatMessage({ id: 'TZiwM1yoTD2siZ-4qWY0', defaultMessage: '{count, plural, one {# Teenager} other {# Teenagers}}', description: 'Pluralised count of teenager travellers in the tour booking occupancy summary' }, { count })
      case 'traveller': return intl.formatMessage({ id: 'yhbVoGULT0FABYYZSr1B', defaultMessage: '{count, plural, one {# traveller} other {# travellers}}', description: 'Pluralised count of travellers displayed in the tour booking occupancy display' }, { count })
      case 'Traveller': return intl.formatMessage({ id: '8vTL5_H9awMdKQ_nLmDC', defaultMessage: '{count, plural, one {# Traveller} other {# Travellers}}', description: 'Pluralised count of travellers displayed in the tour booking occupancy summary' }, { count })
      default: return intl.formatMessage(
        {
          id: 'UE-04vCA9rFNMo_V46Lw',
          defaultMessage: '{count, plural, one {# {type}} other {# {pluralType}}}',
          description: 'Fallback tour traveller type count for unrecognized traveller types',
        },
        { count, type, pluralType: getPlural(type) },
      )
    }
  }
  const travellerTypesStr = travellerTypes.map(type => formatTravellerTypeCount(type, travellersCount[type]))
  return formatInlineList(travellerTypesStr, 'unit')
}

export function countOccupantsForProperty(occupancies: Array<App.Occupants>, property?: App.OfferProperty) {
  return countOccupants(occupancies, {
    maxChildAge: property?.maxChildAge,
    maxInfantAge: property?.maxInfantAge,
    maxTeenagerAge: property?.maxTeenagerAge,
  })
}

export function countOccupantsForBundleAndSave(occupancies: Array<App.Occupants>, offer: App.BundleOffer) {
  const maxTeenagerAge = getBundleAndSaveMaxTeenagerAge(offer)
  const maxChildAge = getBundleAndSaveMaxChildAge(offer)
  const maxInfantAge = getBundleAndSaveMaxInfantAge(offer)
  return countOccupants(occupancies, { maxChildAge, maxInfantAge, maxTeenagerAge })
}
// END OF TODO

/**
 * Consolidates all occupancies into a single record and transforms childrens ages based on
 * the known flight infant/child ages
 * @param occupancies Set of occupancies to count
 * @returns Consolidated occupancy with all sumed up
 */
export function countOccupantsForFlights(occupancies: Array<App.Occupants>) {
  return countOccupants(occupancies, { maxInfantAge: maxFlightInfantAge, maxChildAge: maxFlightChildAge })
}

/**
 * Single-room capacity row for OccupancyPicker from cruise ship occupancy limits
 * (offer-page traveller picker, offer modal, checkout cabin step).
 */
export function buildCruiseShipRoomCapacities(
  shipOccupancies: App.CruiseShip['occupancies'],
): Array<App.RoomCapacity> {
  return [{
    adults: shipOccupancies?.maxAdults || 0,
    infants: shipOccupancies?.maxInfants || 0,
    children: shipOccupancies?.maxChildren || 0,
    teenagers: 0,
    total: shipOccupancies?.maxOccupancy || shipOccupancies?.maxChildren || shipOccupancies?.maxAdults || 0,
  }]
}

export function getMaxOccupancies(
  capacities: Array<App.RoomCapacity> = [{ adults: 14, children: 13, infants: 13, teenagers: 13 }],
  existingOccupants: App.Occupants,
  options: CountOccupantsOptions = {},
) {
  const totalOccupants = countOccupants([existingOccupants], options)
  const compatibleCapacities = capacities.filter(
    capacity => (
      totalOccupants.adults <= capacity.adults &&
      totalOccupants.children <= capacity.children &&
      // teenagers can be counted as adults when teenager capacity is not explicitly set
      // this allows teenager occupants (created by maxTeenagerAge property rules) to fall back to adult capacity
      totalOccupants.teenagers + totalOccupants.adults <= capacity.teenagers + capacity.adults &&
      // infants can be counted as children, but children cannot be counted as infants
      // so infants can take the occupant of the children one
      totalOccupants.infants + totalOccupants.children <= capacity.infants + capacity.children
    ),
  )
  if (!isNonEmptyArray(compatibleCapacities)) {
    return {
      adults: totalOccupants.adults,
      teenagers: totalOccupants.teenagers,
      children: totalOccupants.children,
      infants: totalOccupants.infants,
    }
  }

  const maxOccupancy = max(compatibleCapacities, c => c.total ?? (c.adults + c.teenagers + c.children + c.infants)) || compatibleCapacities[0]
  const occupancyLimit = min([MAX_ADULTS, maxOccupancy.total ?? maxOccupancy.adults + maxOccupancy.children + maxOccupancy.infants + maxOccupancy.teenagers])
  const totalPeople = totalOccupants.adults + totalOccupants.children + totalOccupants.infants + totalOccupants.teenagers

  if (totalPeople < occupancyLimit) {
    return {
      adults: max(compatibleCapacities, c => c.adults).adults,
      teenagers: max(compatibleCapacities, c => c.teenagers).teenagers,
      children: max(compatibleCapacities, c => c.children).children,
      infants: max(compatibleCapacities, c => c.infants).infants,
    }
  }

  return {
    adults: totalOccupants.adults,
    teenagers: totalOccupants.teenagers,
    children: totalOccupants.children,
    infants: totalOccupants.infants,
  }
}

/**
 * Returns the largest guest count from the list of capacities
 * @param capacities The capacities to check through
 */
export function getMaxGuestCount(capacities: Array<App.RoomCapacity> = []) {
  return max(capacities.map(c => c.adults + c.teenagers + c.children + c.infants)) ?? 0
}

export function getMaxNonInfantGuestCount(capacities: Array<App.RoomCapacity>): number {
  return max(capacities.map(c => c.adults + c.teenagers + c.children)) ?? 0
}

/**
 * Returns the min and max age limits for ChildAgeSelect following the max room capacity
 * @param capacities Set of available capacities for the room
 * @param existingOccupants Current occupants objets for the room
 * @param childIndex Selector position inside existingOccupants.childrenAge
 * @param maxInfantAge Oldest permissible age for infants, or -1 if they should be treated as regular children
 * @param maxChildAge Oldest permissible age for regular children
 * @param maxTeenagerAge Oldest permissible age for teenagers, or -1/`maxChildAge` if they should be treated as regular children
 * @returns a object with the max and min age limits for age selector
 */
export function getChildrenAgesLimits(
  capacities: Array<App.RoomCapacity> = [{ adults: MAX_ADULTS, children: 13, infants: 13, teenagers: 13 }],
  existingOccupants: App.Occupants,
  childIndex: number,
  maxInfantAge: number,
  maxChildAge: number,
  maxTeenagerAge: number,
) {
  // AgeSelect from GlobalSearch doesn't use maxInfantAge or capacities
  // So we just return the max as maxChildAge or maxTeenagerAge
  if (maxInfantAge === -1 || !capacities.length) {
    return { max: Math.max(maxTeenagerAge, maxChildAge), min: 0 }
  }
  const totalOccupants = countOccupants([existingOccupants], {
    maxInfantAge,
    maxChildAge,
    maxTeenagerAge,
  })
  const compatibleCapacities = capacities.filter(capacity => (
    totalOccupants.adults <= capacity.adults
  ))
  if (!isNonEmptyArray(compatibleCapacities)) {
    return { max: Math.max(maxTeenagerAge, maxChildAge), min: 0 }
  }

  const maxChildCapacities = {
    teenagers: max(compatibleCapacities, c => c.teenagers).teenagers,
    children: max(compatibleCapacities, c => c.children).children,
    infants: max(compatibleCapacities, c => c.infants).infants,
  }

  const selectedAge = totalOccupants.childrenAge[childIndex]
  const unselectedAges = totalOccupants.childrenAge.filter(age => age === -1).length
  const isChildSelected = selectedAge > maxInfantAge && selectedAge <= maxChildAge
  const isInfantSelected = selectedAge <= maxInfantAge && selectedAge !== -1
  const isTeenagerSelected = selectedAge > maxChildAge && selectedAge <= maxTeenagerAge

  // If a children is already selected, the max limit needs to be maxChildAge if user
  // wants to change the age. The same happens with the min limit being 0 if a infant
  // is already selected. Otherwise, the limits will works with the available capacity.
  // If no longer have capacity for select Children, max limit will be maxInfantAge and just
  // infants can be selected. In the same way, if no longer have capacity for select infants
  // the min limit will be (maxInfantAge + 1) for just allow select children.

  const availableCapacity = {
    teenagers: maxChildCapacities.teenagers - totalOccupants.teenagers,
    children: maxChildCapacities.children - totalOccupants.children,
    infants: maxChildCapacities.infants - (totalOccupants.infants - unselectedAges),
  }

  let maxAgeLimit = Math.max(maxTeenagerAge, maxChildAge)

  if (!isTeenagerSelected && availableCapacity.teenagers <= 0) {
    maxAgeLimit = maxChildAge
    if (!isChildSelected && availableCapacity.children <= 0) {
      maxAgeLimit = maxInfantAge
    }
  }

  let minAgeLimit = 0

  if (!isInfantSelected && availableCapacity.infants <= 0) {
    minAgeLimit = maxInfantAge + 1
    if (!isChildSelected && availableCapacity.children <= 0) {
      minAgeLimit = maxChildAge + 1
    }
  }

  return { max: maxAgeLimit, min: minAgeLimit }
}

/**
 * Aggregates and formats occupancies into a format that describes
 * the totals of each type of guest.
 *
 * This function only works for English language. All usages should
 * eventually be converted to use the translated version.
 *
 * @param occupants The occupancies to format
 * @returns Formatted string of occupants
 */
export function formatOccupantsShort(occupants?: Array<App.Occupants> | App.Occupants, options?: CountOccupantsOptions, adultLabel: AdultLabel = 'adult') {
  const filteredOccupants = nonNullable(Array.isArray(occupants) ? occupants : [occupants])
  const { adults, children, infants, teenagers } = countOccupants(filteredOccupants, options)

  if (!occupants || (adults === 0 && children === 0 && infants === 0 && teenagers === 0)) {
    return ''
  }

  const intl = getCurrentIntl()
  const adultLabelFormatted = (() => {
    switch (adultLabel) {
      case 'adult':
        return intl.formatMessage({
          id: 'HL6sBoA9zDPdX-Xv8e1p',
          defaultMessage: '{count, plural, one {# adult} other {# adults}}',
          description: 'Occupancy short format adult count in the occupancy or guest picker',
        }, { count: adults })
      case 'traveller':
        return intl.formatMessage({
          id: '41Hp_4ZQ4a59tf0r-XP5',
          defaultMessage: '{count, plural, one {# traveller} other {# travellers}}',
          description: 'Occupancy short format traveller count in the traveller details form',
        }, { count: adults })
      default:
        return intl.formatMessage({
          id: 'eQdiym2oPvQT0Nuv0-2s',
          defaultMessage: '{count, plural, one {# guest} other {# guests}}',
          description: 'Occupancy short format guest count in the occupancy or guest picker',
        }, { count: adults })
    }
  })()
  const parts = [
    adultLabelFormatted,
    teenagers > 0 ? intl.formatMessage({
      id: 'FHVZ7nSfG1xsXexuMOzi',
      defaultMessage: '{count, plural, one {# teenager} other {# teenagers}}',
      description: 'Occupancy short format teenager count in the occupancy or guest picker',
    }, { count: teenagers }) : '',
    children > 0 ? intl.formatMessage({
      id: 'Ipel-R4JRCzU-YEiSpp-',
      defaultMessage: '{count, plural, one {# child} other {# children}}',
      description: 'Occupancy short format child count in the occupancy or guest picker',
    }, { count: children }) : '',
    infants > 0 ? intl.formatMessage({
      id: 'sIAtmJU9v0SG1-K49JaT',
      defaultMessage: '{count, plural, one {# infant} other {# infants}}',
      description: 'Occupancy short format infant count in the occupancy or guest picker',
    }, { count: infants }) : '',
  ].filter(Boolean)
  return formatInlineList(parts, 'unit')
}

/**
 * Formats the occupancys based on the age requirements of a given property
 * @param occupants The occupancies to format
 * @param property The property to get max age data for
 * @returns
 */
export function formatOccupantsForProperty(occupants: Array<App.Occupants> | App.Occupants, property?: CountOccupantsOptions) {
  return formatOccupantsShort(occupants, property ? { maxChildAge: property.maxChildAge, maxInfantAge: property.maxInfantAge, maxTeenagerAge: property.maxTeenagerAge } : undefined)
}

/**
 * Aggregates and formats occupancies into a simplified format of
 * adults if only adults, or total number of guests when children/infants
 * @param occupants The occupancies to format
 * @returns Formatted string of occupants
 */
export function formatOccupantsAsGuests(...occupants: Array<App.Occupants>) {
  const intl = getCurrentIntl()
  const { adults, children, infants, teenagers } = countOccupants(occupants)

  if (children || infants || teenagers) {
    return intl.formatMessage({
      id: 'rctML4UVWbaWNqmk9a6V',
      defaultMessage: '{count, plural, one {# guest} other {# guests}}',
      description: 'Occupancy total guest count including children and infants',
    }, { count: adults + children + infants + teenagers })
  } else {
    return intl.formatMessage({
      id: 'tWhY5TIGNR1NXSJREzaf',
      defaultMessage: '{count, plural, one {# adult} other {# adults}}',
      description: 'Pluralised count of adults for the occupancy display when only adults are present',
    }, { count: adults })
  }
}

export function formatOccupantsAsTravellers(...occupants: Array<App.Occupants>) {
  const intl = getCurrentIntl()
  const { adults, children, infants, teenagers } = countOccupants(occupants)

  return intl.formatMessage({
    id: 'lJX5URVZGD5RJ5ch4I2A',
    defaultMessage: '{count, plural, one {# traveller} other {# travellers}}',
    description: 'Occupancy total traveller count in the traveller details form',
  }, { count: adults + children + infants + teenagers })
}

export function formatOccupantsForSelectionBox(isLeTour: boolean = false, ...occupants: Array<App.Occupants>) {
  const intl = getCurrentIntl()
  const { adults, children, infants, teenagers } = countOccupants(occupants)

  if (children || infants || teenagers) {
    return intl.formatMessage({
      id: 'CY5wVVKnXoRgc25fueTG',
      defaultMessage: '{count, plural, one {# guest} other {# guests}}',
      description: 'Selection box total guest count in the offer section',
    }, { count: adults + children + infants + teenagers })
  } else if (isLeTour) {
    return intl.formatMessage({
      id: 'n5rEkLIQGDAoyC2Er0mZ',
      defaultMessage: '{count, plural, one {# traveller} other {# travellers}}',
      description: 'Selection box traveller count for tours on the tour offer detail page',
    }, { count: adults })
  } else {
    return intl.formatMessage({
      id: 'IodR1N6B5u3HyiDfAzuO',
      defaultMessage: '{count, plural, one {# adult} other {# adults}}',
      description: 'Pluralised count of adults displayed in the occupancy selection box or dropdown',
    }, { count: adults })
  }
}

/**
 * Tour V1 occupancy must be one adult per "room"
 */
export function isValidTourV1Occupancy(occupants: Array<App.Occupants>) {
  return occupants.every(occ => (
    occ.adults === 1 && (occ.children ?? 0) === 0 && (occ.infants ?? 0) === 0
  ))
}

export function isValidAccommodationSearchOccupancy(occupants: Array<App.Occupants>, maxAge: number) {
  return occupants.every(occ => occ.childrenAge?.every(age => (age >= 0) && (age <= maxAge)))
}

export function isValidFlightsOccupants(occupants: App.Occupants) {
  const areChildAgesValid = occupants.childrenAge?.every(i => (i >= 0) && (i <= maxFlightChildAge))

  return !validateFlightOccupants(occupants) && areChildAgesValid
}

export function areOccupantsEqual(
  ...occupants: Array<App.Occupants>
): boolean {
  const first = occupants[0]
  return !!first && occupants.every(occ => (
    occ.adults === first.adults &&
    occ.teenagers === first.teenagers &&
    occ.children === first.children &&
    occ.infants === first.infants &&
    isShallowEqual(occ.childrenAge ?? [], first.childrenAge ?? [])
  ))
}

/**
 * Normalizes a value by treating undefined, 0, and empty arrays as equivalent (returns undefined)
 */
function normalizeOccupancyValue(value: number | Array<number> | undefined): number | Array<number> | undefined {
  if (value === undefined || value === 0) return undefined
  if (Array.isArray(value) && value.length === 0) return undefined
  return value
}

/**
 * Compares two occupants, treating undefined, 0, and empty arrays as equivalent
 */
function areSingleOccupantsEqualNormalized(occ1: App.Occupants, occ2: App.Occupants): boolean {
  return (
    normalizeOccupancyValue(occ1.adults) === normalizeOccupancyValue(occ2.adults) &&
    normalizeOccupancyValue(occ1.teenagers) === normalizeOccupancyValue(occ2.teenagers) &&
    normalizeOccupancyValue(occ1.children) === normalizeOccupancyValue(occ2.children) &&
    normalizeOccupancyValue(occ1.infants) === normalizeOccupancyValue(occ2.infants) &&
    isShallowEqual(
      normalizeOccupancyValue(occ1.childrenAge) as Array<number> | undefined ?? [],
      normalizeOccupancyValue(occ2.childrenAge) as Array<number> | undefined ?? [],
    )
  )
}

/**
 * Compares two arrays of occupancies, treating undefined, 0, and empty arrays as equivalent
 * @param arr1 First array of occupancies
 * @param arr2 Second array of occupancies
 * @returns true if arrays are deeply equal (with normalization)
 */
export function areOccupancyArraysEqual(
  arr1: Array<App.Occupants> | undefined,
  arr2: Array<App.Occupants> | undefined,
): boolean {
  if (arr1 === arr2) return true
  if (!arr1 || !arr2) return false
  if (arr1.length !== arr2.length) return false

  return arr1.every((occ1, idx) => areSingleOccupantsEqualNormalized(occ1, arr2[idx]))
}

function mergeOccupantsReducer(a: App.Occupants, b: App.Occupants): App.Occupants {
  return {
    adults: a.adults + b.adults,
    teenagers: (a?.teenagers ?? 0) + (b?.teenagers ?? 0),
    children: (a?.children ?? 0) + (b?.children ?? 0),
    infants: (a?.infants ?? 0) + (b?.infants ?? 0),
    childrenAge: [...(a?.childrenAge ?? []), ...(b?.childrenAge ?? [])],
  }
}

export function mergeOccupants(occupants_list: Array<App.Occupants>): App.Occupants {
  const [first, ...rest] = occupants_list
  return rest.reduce(mergeOccupantsReducer, first)
}

export function getOccupanciesFromReservations(...reservations: Array<App.OrderReservation>): App.Occupants {
  return reservations.reduce((acc, reservation) => {
    /**
     * In some Cruise V1 scenarios, the cruise may be restricted to adults only.
     * In these cases, the reservation does not specify the number of adults, children, or infants.
     * Therefore, we assume the reservation is for one adult by default.
     */
    if (!reservation.adults && !reservation.teenagers && !reservation.children && !reservation.infants) {
      return {
        adults: acc.adults + 1,
        children: 0,
        infants: 0,
        teenagers: 0,
      }
    }

    return {
      adults: acc.adults + reservation.adults,
      teenagers: acc.teenagers + (reservation.teenagers || 0),
      children: acc.children + (reservation.children || 0),
      infants: acc.infants + (reservation.infants || 0),
    }
  }, {
    adults: 0,
    children: 0,
    infants: 0,
    teenagers: 0,
  })
}

/**
 * Ensures that every occupants object passed is 'valid'
 * We consider it an invalid object when there is a child who has not had their age set yet
 */
export function isValidOccupancy(...occupants: Array<App.Occupants>) {
  return occupants.every(occupancy => occupancy.childrenAge?.every(age => age !== -1) ?? true)
}

export function occupantsFitRoomCapacity(
  occupancy: App.Occupants,
  capacity: App.RoomCapacity,
  offer: App.Offer | App.OfferSummary | App.BedbankOffer | App.BundledOffer,
) {
  const property = offer.type !== 'bedbank_hotel' ? offer.property : undefined

  const transformedOccupants = countOccupantsForProperty([occupancy], property)

  return (
    transformedOccupants.adults <= capacity.adults &&
    (!transformedOccupants?.teenagers || transformedOccupants.teenagers <= capacity.teenagers) &&
    (!transformedOccupants?.children || transformedOccupants.children <= capacity.children) &&
    (!transformedOccupants?.infants || transformedOccupants.infants <= capacity.infants)
  )
}

export function occupantsFitAnyRoomCapacity(
  occupancy: App.Occupants,
  capacities: Array<App.RoomCapacity>,
  offer: App.Offer | App.OfferSummary | App.BedbankOffer | App.BundledOffer,
) {
  const transformedOccupants = offer.type === 'bedbank_hotel' ?
      countOccupants([occupancy], { maxChildAge: getBedbankChildMaxAge(offer) }) :
      countOccupantsForProperty([occupancy], offer.property)

  return capacities.some(capacity => (
    transformedOccupants.adults <= capacity.adults &&
    (!transformedOccupants?.teenagers || transformedOccupants.teenagers <= capacity.teenagers) &&
    (!transformedOccupants?.children || transformedOccupants.children <= capacity.children) &&
    (!transformedOccupants?.infants || transformedOccupants.infants <= capacity.infants)
  ))
}

export function isSoloOccupancy(occupants: Array<App.Occupants>): boolean {
  return occupants.every(occ => (
    occ.adults === 1 && (occ.children ?? 0) === 0 && (occ.infants ?? 0) === 0 && (occ.teenagers ?? 0) === 0
  ))
}
